import { z } from 'zod';
import { TimestampSchema, UidSchema } from './primitives.js';

/**
 * Staff doc at `tenants/{tid}/outlets/{oid}/staff/{uid}`. Written by:
 *   - `tenantBootstrap` at signup (creates the owner doc in the first outlet)
 *   - `inviteStaff` callable (Sprint 55) for cashier/washer/manager additions
 *   - Direct dashboard writes from owner/manager for role changes + deactivation
 *
 * The doc id IS the user's Firebase Auth uid. `claimsSyncStaff` watches every
 * write and aggregates roles across all of a tenant's outlets into a single
 * `claims.roles[tenantId]` using highest-privilege-wins
 * (owner > manager > cashier > washer).
 *
 * Setting `active: false` (instead of deleting) preserves the audit trail
 * — the operator can see "fired 2026-04-15 by Aiman" — while
 * `claimsSyncStaff` still revokes the role claim.
 */

export const StaffRoleSchema = z.enum(['owner', 'manager', 'cashier', 'washer']);
export type StaffRole = z.infer<typeof StaffRoleSchema>;

export const StaffDocSchema = z.object({
  // The repo layer injects the Firestore doc id as `{ id: d.id, ...data }`
  // before parsing (see dashboard `_base.ts` get/list/listSnapshot). Zod's
  // default `z.object` STRIPS any key it doesn't declare, so without this
  // field the id was silently dropped — `staff.id` came back `undefined`, the
  // /staff "You" self-pill (`s.id === currentUid`) never showed, and
  // role-change / deactivate called `update(staff.id, …)` with an undefined
  // id → broken staff management. Declaring `id` here lets the injected value
  // survive the parse. Optional because writes via `.set()` pass a value
  // object without an id (id is never a stored field). Same defect + fix as
  // the outlet doc (PR #50). Found by the dashboard e2e harness, 2026-07-17.
  id: z.string().optional(),
  role: StaffRoleSchema,
  active: z.boolean().default(true),
  addedAt: TimestampSchema,
  /** Uid of the operator who invited this staff member. Null for the owner-at-signup. */
  addedBy: UidSchema.nullable().optional(),
  /** Display name captured at invite time so the /staff list renders without an extra fetch. */
  displayName: z.string().max(80).optional(),
  /** E.164 phone snapshot — same reason as displayName. */
  phone: z.string().max(20).optional(),
});
export type StaffDoc = z.infer<typeof StaffDocSchema>;
